import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Upload, X, Plus, ClipboardPaste, List } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Compact modal that lets the admin add recipients to a specific campaign
 * without having to open the full wizard editor. Supports:
 *  - Import from one or more saved Contact Lists
 *  - Upload CSV
 *  - Paste emails
 */
export default function QuickImportRecipientsModal({ open, onClose, campaign, onDone }) {
  const token = () => localStorage.getItem('admin_token');
  const [contactLists, setContactLists] = useState([]);
  const [selectedListIds, setSelectedListIds] = useState([]);
  const [availableTags, setAvailableTags] = useState([]); // aggregated tags across selected lists
  const [selectedTags, setSelectedTags] = useState([]);
  const [matchAllTags, setMatchAllTags] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const csvRef = useRef(null);

  const fetchLists = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/email/lists`, { headers: { Authorization: `Bearer ${token()}` } });
      if (r.ok) setContactLists(await r.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelectedListIds([]);
    setSelectedTags([]);
    setAvailableTags([]);
    setPasteText('');
    setLastResult(null);
    fetchLists();
  }, [open, fetchLists]);

  // When list selection changes, aggregate available tags
  useEffect(() => {
    if (!selectedListIds.length) { setAvailableTags([]); setSelectedTags([]); return; }
    const load = async () => {
      const results = await Promise.all(selectedListIds.map(id =>
        fetch(`${API}/api/email/lists/${id}/tags`, { headers: { Authorization: `Bearer ${token()}` } })
          .then(r => r.ok ? r.json() : [])
          .catch(() => [])
      ));
      const merged = new Map();
      for (const rows of results) {
        for (const r of (rows || [])) {
          const k = (r.tag || '').toLowerCase();
          if (!k) continue;
          if (merged.has(k)) merged.get(k).count += r.count;
          else merged.set(k, { tag: r.tag, count: r.count });
        }
      }
      setAvailableTags(Array.from(merged.values()).sort((a, b) => b.count - a.count));
    };
    load();
  }, [selectedListIds]);

  if (!open || !campaign) return null;

  const importLists = async () => {
    if (!selectedListIds.length) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/email/lists/import-to-campaign`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaign_id: campaign.id, list_ids: selectedListIds, tags: selectedTags, match_all_tags: matchAllTags }),
      });
      const data = await r.json();
      if (r.ok) {
        setLastResult({ label: selectedTags.length ? `${selectedListIds.length} list × ${selectedTags.length} tag${selectedTags.length === 1 ? '' : 's'}` : `${selectedListIds.length} list${selectedListIds.length === 1 ? '' : 's'}`, ...data });
        toast.success(`Imported ${data.inserted} recipient(s)`);
        setSelectedListIds([]);
        setSelectedTags([]);
        onDone?.();
      } else {
        toast.error(data.detail || 'Import failed');
      }
    } catch {
      toast.error('Import failed');
    }
    setBusy(false);
  };

  const uploadCSV = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API}/api/email/campaigns/${campaign.id}/recipients/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
        body: fd,
      });
      const data = await r.json();
      if (r.ok) {
        setLastResult({ label: 'CSV', inserted: data.valid, ...data });
        toast.success(`Imported ${data.valid} recipient(s)`);
        onDone?.();
      } else {
        toast.error(data.detail || 'CSV import failed');
      }
    } catch {
      toast.error('CSV import failed');
    }
    setBusy(false);
    if (csvRef.current) csvRef.current.value = '';
  };

  const importPaste = async () => {
    if (!pasteText.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/email/campaigns/${campaign.id}/recipients/paste`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: pasteText }),
      });
      const data = await r.json();
      if (r.ok) {
        setLastResult({ label: 'Paste', inserted: data.valid, ...data });
        toast.success(`Imported ${data.valid} recipient(s)`);
        setPasteText('');
        onDone?.();
      } else {
        toast.error(data.detail || 'Paste import failed');
      }
    } catch {
      toast.error('Paste import failed');
    }
    setBusy(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="quick-import-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-800">Add recipients</h3>
            <p className="text-xs text-gray-500 truncate">Campaign: <span className="font-medium">{campaign.name}</span></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="close-quick-import"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Option 1: Contact Lists */}
          <section>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs uppercase tracking-wide text-gray-600 font-semibold">Import from a saved list</Label>
              <a href="/admin/email/lists" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-700 hover:underline">Manage lists →</a>
            </div>
            {contactLists.length === 0 ? (
              <div className="text-xs text-gray-500 bg-gray-50 border rounded p-3">
                No saved lists yet. <a href="/admin/email/lists" target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">Create your first list</a> to reuse recipients across campaigns.
              </div>
            ) : (
              <>
                <div className="max-h-40 overflow-y-auto border rounded-lg divide-y" data-testid="quick-lists-picker">
                  {contactLists.map(l => (
                    <label key={l.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedListIds.includes(l.id)}
                        onChange={(e) => setSelectedListIds(prev => e.target.checked ? [...prev, l.id] : prev.filter(x => x !== l.id))}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 truncate">{l.name}</div>
                        {l.description && <div className="text-xs text-gray-500 truncate">{l.description}</div>}
                      </div>
                      <span className="text-xs text-gray-500">{l.contact_count}</span>
                    </label>
                  ))}
                </div>

                {availableTags.length > 0 && (
                  <div className="mt-3 p-3 border rounded-lg bg-white" data-testid="quick-tag-filter">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="text-xs font-medium text-gray-700">Filter by tag <span className="text-gray-400 font-normal">(optional)</span></div>
                      <label className="flex items-center gap-1 text-xs text-gray-500 cursor-pointer">
                        <input type="checkbox" checked={matchAllTags} onChange={e => setMatchAllTags(e.target.checked)} className="w-3 h-3" data-testid="quick-match-all-toggle" />
                        Match all (AND)
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {availableTags.map(t => {
                        const active = selectedTags.includes(t.tag);
                        return (
                          <button key={t.tag} onClick={() => setSelectedTags(prev => active ? prev.filter(x => x !== t.tag) : [...prev, t.tag])} data-testid={`quick-tag-${t.tag}`}
                            className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${active ? 'bg-blue-900 border-blue-900 text-white' : 'bg-white border-gray-300 text-gray-700 hover:border-blue-400'}`}>
                            {t.tag} <span className="opacity-70">({t.count})</span>
                          </button>
                        );
                      })}
                    </div>
                    {selectedTags.length > 0 && (
                      <div className="text-xs text-gray-500 mt-1.5">
                        Will import contacts with {matchAllTags ? 'ALL' : 'ANY'} of: <span className="font-medium text-gray-700">{selectedTags.join(', ')}</span>
                      </div>
                    )}
                  </div>
                )}

                <Button size="sm" onClick={importLists} disabled={!selectedListIds.length || busy} className="mt-2 bg-blue-900 hover:bg-blue-800 text-white" data-testid="quick-import-lists-btn">
                  <List className="w-3.5 h-3.5 mr-1" /> Import from {selectedListIds.length} list{selectedListIds.length === 1 ? '' : 's'}{selectedTags.length ? ` · ${selectedTags.length} tag${selectedTags.length === 1 ? '' : 's'}` : ''}
                </Button>
              </>
            )}
          </section>

          {/* Option 2: CSV */}
          <section className="border-t pt-4">
            <Label className="text-xs uppercase tracking-wide text-gray-600 font-semibold">Upload CSV</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-2">Required column: <code className="bg-gray-100 px-1 rounded">email</code>. Optional: first_name, last_name, company, role, type.</p>
            <div onClick={() => csvRef.current?.click()} className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid="quick-csv-drop">
              <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
              <p className="text-xs text-gray-500">{busy ? 'Uploading…' : 'Click to upload a CSV file'}</p>
            </div>
            <input ref={csvRef} type="file" accept=".csv" className="hidden" onChange={(e) => uploadCSV(e.target.files?.[0])} data-testid="quick-csv-input" />
          </section>

          {/* Option 3: Paste */}
          <section className="border-t pt-4">
            <Label className="text-xs uppercase tracking-wide text-gray-600 font-semibold">Paste emails</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-2">One per line or comma-separated.</p>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              className="w-full border rounded-lg p-2 text-xs resize-none"
              rows={4}
              placeholder="alice@corp.com&#10;bob@corp.com, carol@corp.com"
              data-testid="quick-paste-text"
            />
            <Button size="sm" onClick={importPaste} disabled={!pasteText.trim() || busy} className="mt-2 bg-blue-900 hover:bg-blue-800 text-white" data-testid="quick-paste-btn">
              <ClipboardPaste className="w-3.5 h-3.5 mr-1" /> Import Emails
            </Button>
          </section>

          {lastResult && (
            <div className="border rounded-lg bg-green-50 border-green-200 p-3 text-xs text-green-800" data-testid="quick-import-result">
              <span className="font-semibold">Last import ({lastResult.label}):</span> {lastResult.inserted} added
              {lastResult.duplicates != null && `, ${lastResult.duplicates} duplicate${lastResult.duplicates === 1 ? '' : 's'}`}
              {lastResult.suppressed != null && `, ${lastResult.suppressed} suppressed`}
              {lastResult.invalid != null && `, ${lastResult.invalid} invalid`}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between sticky bottom-0">
          <a href={`/admin/email/edit/${campaign.id}`} className="text-xs text-blue-700 hover:underline">Open full campaign editor →</a>
          <Button size="sm" variant="outline" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}
